import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Send, Eye, MousePointerClick, XCircle, UserMinus, TrendingUp,
} from "lucide-react";

function pct(x, y) { return y > 0 ? Math.round((x / y) * 1000) / 10 : 0; }
function fmt(n) { return Number(n || 0).toLocaleString("es-MX"); }

export default function EmailCampaignMetrics() {
  const { id } = useParams();
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const base = superadmin ? "/app/superadmin" : "/app/staff";

  const [campaign, setCampaign] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        api.get(`/admin/email-campaigns/${id}`),
        api.get(`/admin/email-campaigns/${id}/metrics`),
      ]);
      setCampaign(c.data);
      setMetrics(m.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  const loadDeliveries = async () => {
    try {
      const params = new URLSearchParams({ campaign_id: id, limit: "50" });
      if (statusFilter) params.set("status", statusFilter);
      if (search) params.set("search", search);
      const r = await api.get(`/admin/email-deliveries?${params.toString()}`);
      setDeliveries(r.data.items || []);
    } catch (_) {}
  };
  useEffect(() => { if (campaign) loadDeliveries(); }, [campaign, statusFilter, search]);

  if (loading || !campaign || !metrics) {
    return (
      <StaffLayout user={user} superadmin={superadmin}>
        <p className="text-center text-gray-400 py-12">Cargando métricas…</p>
      </StaffLayout>
    );
  }

  const m = metrics.metrics;
  const r = metrics.rates;
  // Chart bars simple SVG
  const chartData = metrics.timeseries.slice(0, 48); // primeras 48h
  const maxVal = Math.max(1, ...chartData.map((h) => h.opened + h.clicked));

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div className="mb-4">
        <Link to={`${base}/email/campaigns`} className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white">
          <ArrowLeft className="w-3 h-3" /> Volver a campañas
        </Link>
      </div>

      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-primary font-semibold">Métricas · {campaign.status}</p>
        <h1 className="mt-1 text-3xl font-bold text-white font-display">{campaign.name}</h1>
        <p className="mt-1 text-sm text-gray-400">
          Plantilla: <Link to={`${base}/email/templates/${campaign.template_id}`} className="text-primary hover:underline">{campaign.template?.name}</Link>
        </p>
      </div>

      {/* 5 KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <KpiCard label="Enviados" value={fmt(m.sent)} icon={Send} />
        <KpiCard label="Aperturas" value={`${r.open_rate}%`} subtitle={`${fmt(m.opened)} únicos`} icon={Eye} accent />
        <KpiCard label="Clicks" value={`${r.click_rate}%`} subtitle={`${fmt(m.clicked)} únicos`} icon={MousePointerClick} accent />
        <KpiCard label="Bounces" value={`${r.bounce_rate}%`} subtitle={`${fmt(m.bounced)} rebotes`} icon={XCircle} />
        <KpiCard label="Bajas" value={`${r.unsub_rate}%`} subtitle={`${fmt(m.unsubscribed)} unsub`} icon={UserMinus} />
      </div>

      {/* Chart */}
      <div className="mb-6 p-4 rounded-xl border border-gray-800 bg-[#111827]">
        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-3 flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> Eventos por hora post-envío (primeras 48h)
        </p>
        <div className="h-32 flex items-end gap-0.5" data-testid="metrics-chart">
          {chartData.map((h) => {
            const openedH = (h.opened / maxVal) * 100;
            const clickedH = (h.clicked / maxVal) * 100;
            return (
              <div key={h.hour} className="flex-1 flex flex-col-reverse gap-0.5" title={`h${h.hour}: ${h.opened} opens · ${h.clicked} clicks`}>
                <div className="bg-primary" style={{ height: `${openedH}%` }} />
                <div className="bg-emerald-500" style={{ height: `${clickedH}%` }} />
              </div>
            );
          })}
        </div>
        <div className="mt-2 flex gap-4 text-[10px] text-gray-500 uppercase tracking-widest font-bold">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-primary inline-block" /> Aperturas</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 inline-block" /> Clicks</span>
        </div>
      </div>

      {/* Deliveries table */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Detalle destinatarios</p>
          <div className="flex-1"></div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar email…"
            data-testid="deliveries-search"
            className="bg-[#111827] border border-gray-800 rounded-md px-2 py-1 text-xs text-white focus:outline-none focus:border-primary"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="deliveries-status"
            className="bg-[#111827] border border-gray-800 rounded-md px-2 py-1 text-xs text-white"
          >
            <option value="">Todos los estados</option>
            <option value="sent">Enviado</option>
            <option value="opened">Abierto</option>
            <option value="clicked">Clickeado</option>
            <option value="bounced">Bounce</option>
          </select>
        </div>

        <div className="overflow-x-auto border border-gray-800 rounded-xl" data-testid="deliveries-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0B0F14] text-xs uppercase tracking-widest text-gray-400">
              <tr>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Estado</th>
                <th className="text-left px-3 py-2">Abierto</th>
                <th className="text-left px-3 py-2">Click</th>
                <th className="text-left px-3 py-2">Enviado</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.length === 0 && (
                <tr><td colSpan={5} className="text-center text-gray-500 py-6">Sin deliveries.</td></tr>
              )}
              {deliveries.map((d) => (
                <tr key={d._id} className="border-t border-gray-800 hover:bg-white/5">
                  <td className="px-3 py-2 text-white text-xs">{d.email_to}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={d.status} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-400 font-mono">{d.opened_at ? d.opened_at.slice(0, 16) : "—"}</td>
                  <td className="px-3 py-2 text-xs text-gray-400 font-mono">{d.clicked_at ? d.clicked_at.slice(0, 16) : "—"}</td>
                  <td className="px-3 py-2 text-xs text-gray-500 font-mono">{d.created_at?.slice(0, 16)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </StaffLayout>
  );
}

function KpiCard({ label, value, subtitle, icon: Icon, accent }) {
  return (
    <div className="p-4 rounded-xl border border-gray-800 bg-[#111827]">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 font-semibold">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p className={`mt-2 text-2xl font-extrabold tabular-nums ${accent ? "text-primary" : "text-white"}`}>
        {value}
      </p>
      {subtitle && <p className="text-[10px] text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    sent:      { bg: "bg-gray-500/10", text: "text-gray-400", border: "border-gray-500/30", label: "SENT" },
    delivered: { bg: "bg-gray-500/10", text: "text-gray-400", border: "border-gray-500/30", label: "DELIVERED" },
    opened:    { bg: "bg-primary/10", text: "text-primary", border: "border-primary/30", label: "OPENED" },
    clicked:   { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30", label: "CLICKED" },
    bounced:   { bg: "bg-destructive/10", text: "text-destructive", border: "border-destructive/30", label: "BOUNCED" },
    failed:    { bg: "bg-destructive/10", text: "text-destructive", border: "border-destructive/30", label: "FAILED" },
  }[status] || { bg: "bg-gray-500/10", text: "text-gray-400", border: "border-gray-500/30", label: status?.toUpperCase() };
  return (
    <span className={`text-[9px] px-2 py-0.5 rounded uppercase font-bold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}
